import React from 'react'
import classnames from 'classnames'

import { IProps } from './index'
import { RichText } from '../RichText'
import { Text } from '../Text'
import { TextLink } from '../TextLink'
import { LegacyColor, fontHoney, fontSmileScript, getColorTheme, mq } from '../../utils'
import statisticBackground from '../../images/statistic-background.svg'

import { Statistic as StatisticDefault } from './component'
import { BrandOptions, useBrand } from '../../providers/brandProvider'

export const Statistic = (props: IProps): React.ReactElement => {
  const theme = useBrand()
  if (theme === BrandOptions.default) {
    return <StatisticDefault {...props} />
  }
  return <StatisticLegacy {...props} />
}

const StatisticLegacy: React.FC<IProps> = ({
  parentVariation,
  variation,
  stat,
  statImage,
  eyebrow,
  description,
  credit,
  parentBackgroundColor,
  backgroundColor,
  backgroundImage,
  isDecorativeImage,
  shouldLocalise,
}: IProps) => {
  const shouldRenderText = !statImage || (shouldLocalise && !isDecorativeImage)

  const renderStatisticOrImage = () => {
    if (shouldRenderText) {
      return (
        <>
          <div className="statistic__handwritten-title">
            <RichText html={stat} />
          </div>
          <style jsx>{`
            :global(.statistic__stat .statistic__handwritten-title) {
              font-family: ${fontHoney};
              font-size: 3rem;
              font-weight: 800;
            }

            :global(:not(.stat) .statistic__handwritten-title) {
              margin: 0 auto;
              text-align: center;
            }

            :global(.statistic__stat .statistic__handwritten-title span) {
              font-family: ${fontSmileScript};
              font-size: 3rem;
              font-feature-settings: 'liga' 1;
            }

            :global(.statistics--splitted-card .statistic__stat .statistic__handwritten-title),
            :global(.statistic--splitted-card .statistic__stat .statistic__handwritten-title) {
              font-size: 2.5rem;
              font-weight: 700;
            }

            :global(.statistics--splitted-card .statistic__stat .statistic__handwritten-title span),
            :global(.statistic--splitted-card .statistic__stat .statistic__handwritten-title span) {
              font-size: 1.25rem;
              font-weight: 400;
            }

            :global(.statistics--splitted-card .statistic__description span),
            :global(.statistic--splitted-card .statistic__description span) {
              font-size: 0.8125rem;
            }

            @media (${mq.tablet}) {
              :global(.statistics--splitted-card .statistic__stat .statistic__handwritten-title),
              :global(.statistic--splitted-card .statistic__stat .statistic__handwritten-title) {
                font-size: 2.5rem;
              }

              // eslint-disable-next-line prettier/prettier
              :global(.statistics--splitted-card .statistic__stat .statistic__handwritten-title span),
              // eslint-disable-next-line prettier/prettier
              :global(.statistic--splitted-card .statistic__stat .statistic__handwritten-title span) {
                font-size: 1.25rem;
              }
            }

            @media (${mq.desktop}) {
              :global(.statistic__stat .statistic__handwritten-title) {
                font-size: 5.375rem;
              }

              :global(.statistics--splitted-card .statistic__stat .statistic__handwritten-title),
              :global(.statistic--splitted-card .statistic__stat .statistic__handwritten-title) {
                font-size: 3.375rem;
              }

              :global(.statistic__stat .statistic__handwritten-title span),
              // eslint-disable-next-line prettier/prettier
              :global(.statistics--splitted-card .statistic__stat .statistic__handwritten-title span),
              :global(.statistic--splitted-card .statistic__stat .statistic__handwritten-title span) {
                font-size: 2.5rem;
                font-weight: 700;
              }
            }

            @media (${mq.desktopLg}) {
              :global(.statistics--splitted-card .statistic__stat .statistic__handwritten-title),
              :global(.statistic--splitted-card .statistic__stat .statistic__handwritten-title) {
                font-size: 5.375rem;
              }

              :global(.statistic__stat .statistic__handwritten-title span),
              // eslint-disable-next-line prettier/prettier
              :global(.statistics--splitted-card .statistic__stat .statistic__handwritten-title span),
              :global(.statistic--splitted-card .statistic__stat .statistic__handwritten-title span) {
                font-size: 3.5rem;
              }

              :global(.statistics--splitted-card .statistic__description span),
              :global(.statistic--splitted-card .statistic__description span) {
                font-size: 1rem;
              }
            }
          `}</style>
        </>
      )
    }

    return (
      <>
        <img src={statImage} alt={stat} />
        <style jsx>
          {`
            img {
              max-width: 100%;
              max-height: 2.8125rem;
            }

            @media (${mq.tablet}) {
              img {
                max-height: 5.3125rem;
              }
            }
          `}
        </style>
      </>
    )
  }

  const themeColors = parentBackgroundColor
    ? getColorTheme('legacy', parentBackgroundColor)
    : backgroundColor
    ? getColorTheme('legacy', backgroundColor)
    : getColorTheme('legacy', LegacyColor.LightGray)
  const statisticClasses = classnames('statistic', {
    'has-background': backgroundImage,
    'no-background': !backgroundImage,
    [`statistic--${variation}`]: !parentVariation && variation,
    'statistics--standard': !parentVariation && !variation,
  })

  return (
    <div className={statisticClasses} data-testid="statistic">
      <div className="statistic__content-block">
        <p className="statistic__stat">{renderStatisticOrImage()}</p>
      </div>
      {(eyebrow || description || credit) && (
        <div className="statistic__content-block">
          {eyebrow && (
            <div className="statistic__eyebrow">
              <Text size="lg-eyebrow">{eyebrow}</Text>
            </div>
          )}
          {description && (
            <div className="statistic__description">
              <Text size="body">
                <RichText html={description} />
              </Text>
            </div>
          )}
          {credit && (
            <p className="statistic__credit">
              {credit.url ? (
                <TextLink url={credit.url}>
                  <Text size="caption">{credit.text}</Text>
                </TextLink>
              ) : (
                <Text size="caption">{credit.text}</Text>
              )}
            </p>
          )}
        </div>
      )}
      <style jsx>
        {`
          .statistic {
            position: relative;
            display: flex;
            flex-direction: column;
            align-content: center;
            justify-content: center;
            text-align: center;
            padding: 1.25rem 1.75rem;
            color: ${themeColors.textColor};

            &.no-background::before {
              content: '';
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              z-index: -1;
              mask-image: url('${statisticBackground}');
              mask-repeat: no-repeat;
              mask-size: cover;
              background-color: ${themeColors.backgroundColor};
            }

            .statistic__content-block:last-child {
              display: flex;
              flex-direction: column;
            }

            .statistic__content-block:last-child {
              margin-top: 1rem;
            }

            .statistic__eyebrow + .statistic__description {
              margin-top: 0.75rem;
            }

            .statistic__description,
            .statistic__credit {
              display: inline-block;
            }

            .statistic__credit {
              margin-top: 0.875rem;
            }
          }

          .statistic--card {
            height: 100%;
            padding: 1.75rem 1.25rem;
            margin: 0 auto;
            border-radius: 1.5625rem;
            background-color: ${themeColors.backgroundColor};

            &.no-background::before {
              display: none;
            }

            .statistic__handwritten-title {
              display: inline-block;
            }

            .statistic--card .statistic__stat {
              margin-bottom: 0;
            }
          }

          :global(.statistics--cards .statistic),
          :global(.statistics--splitted-cards .statistic),
          .statistic--card,
          .statistic--splitted-card {
            height: 100%;
            padding: 1.25rem;
            margin: 0 auto;
            border-radius: 1.5625rem;
            background-color: ${themeColors.backgroundColor};
          }

          :global(.statistics--cards .statistic.no-background::before),
          :global(.statistics--splitted-cards .statistic.no-background::before),
          .statistic--card.no-background::before,
          .statistic--splitted-card.no-background::before {
            display: none;
          }

          :global(.statistics--splitted-cards .statistic),
          .statistic--splitted-card {
            align-content: stretch;
          }

          :global(.statistics.statistics--splitted-cards .statistic),
          .statistic--splitted-card.no-background {
            display: grid;
            grid-template-columns: 4fr 6fr;
            background-color: ${LegacyColor.BlueExtraLight};
            padding: 0;
          }

          :global(.statistics--splitted-cards .statistic__content-block:first-child),
          :global(.statistics--splitted-cards .statistic__content-block:last-child),
          .statistic--splitted-card .statistic__content-block:first-child,
          .statistic--splitted-card .statistic__content-block:last-child {
            display: flex;
            flex-direction: column;
            justify-content: center;
            height: 100%;
            margin-top: 0;
          }

          :global(.statistics--splitted-cards .statistic__content-block:first-child),
          .statistic--splitted-card .statistic__content-block:first-child {
            padding: 1.25rem;
            border-radius: 1.5625rem 0 0 1.5625rem;
            background-color: ${LegacyColor.LightBlue};
          }

          :global(.statistics--splitted-cards .statistic__content-block:last-child),
          .statistic--splitted-card .statistic__content-block:last-child {
            padding: 1.25rem;
            text-align: left;
          }

          :global(.statistics--splitted-cards .stat),
          .statistic--splitted-card .statistic__stat {
            background-color: ${LegacyColor.LightBlue};
          }

          :global(.statistics--splitted-cards .statistic__eyebrow),
          :global(.statistics--splitted-cards .statistic__description),
          :global(.statistics--splitted-cards .statistic__credit),
          .statistic--splitted-card .statistic__eyebrow,
          .statistic--splitted-card .statistic__description,
          .statistic--splitted-card .statistic__credit {
            grid-column: 2;
            grid-row: 1;
          }

          @media (${mq.laptop}) {
            .statistic {
              min-height: 12.5rem;
              padding: 1.75rem;
            }

            .statistic--splitted-card {
              min-height: auto;
            }
          }

          @media (${mq.desktop}) {
            :global(.statistics--splitted-cards .statistic__content-block:last-child),
            .statistic--splitted-card .statistic__content-block:last-child {
              padding: 1.75rem;
            }

            .statistic__eyebrow + .statistic__description {
              margin-top: 1rem;
            }
          }

          @media (${mq.desktopLg}) {
            :global(.statistics--splitted-cards .statistic__content-block:last-child),
            .statistic--splitted-card .statistic__content-block:last-child {
              padding: 2.625rem;
            }
          }
        `}
      </style>
    </div>
  )
}
